import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ensureCapabilities } from '@/lib/capabilities-api';
import { useMutation } from '@tanstack/react-query';
import { auth, getErrorMessage, isHttpStatus } from '@/lib/platform';
import { useContactPhone } from '@/lib/contact';

import { passesApi } from '@/lib/passes-api';
import { usePassDraft } from '@/lib/use-passes-booking';
import { openRazorpayCheckout, PaymentCancelledError } from '@/lib/razorpay';
import { customerEmail, customerName, razorpayEmail } from '@/lib/customer';
import { formatMoney } from '@/lib/format';
import { categoryLabel } from '@/lib/passes-format';
import type { Pass, PassItemSelection } from '@/lib/passes-types';
import { CategoryPicker, type BrowseParams } from '@/components/passes/category-picker';
import {
  AddOnToggles,
  PassOptionCard,
  QuantityStepper,
} from '@/components/passes/pass-option-card';
import { XInputFormGate } from '@/components/passes/xinput-form-gate';
import { EmptyState } from '@/components/shared/empty-state';
import { Screen, AccountButton } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { SumRow } from '@/components/ui/sum-row';
import { ProgressLabel } from '@/components/ui/progress-label';

// ONDC requires an explicit city code per search; the entry-pass backend defaults to
// Delhi. Confirm the live preprod TRV14 BPP coverage before going live and adjust.
const DEFAULT_CITY = 'std:011';
// Fallback search-centre when the user doesn't share location — without a GPS the engine
// omits the START stop and the chosen visit-time range with it. City centre for
// DEFAULT_CITY (Delhi); keep the two in sync.
const DEFAULT_GPS = '28.6139, 77.2090';

export const Route = createFileRoute('/_authed/passes/')({
  // Passes (TRV14) hard-503 when the domain is off — redirect home rather than
  // dead-end. Fails closed (ensureCapabilities returns all-off on error).
  beforeLoad: async () => {
    const caps = await ensureCapabilities();
    if (!caps.passes) throw redirect({ to: '/' });
  },
  component: BookPassPage,
});

/** Group catalog passes by their provider (the catalog has no clean venue↔item link;
 *  each Pass carries its provider, so provider name is the natural grouping). */
function groupByProvider(passes: Pass[]): { name: string; passes: Pass[] }[] {
  const groups = new Map<string, Pass[]>();
  for (const p of passes) {
    const key = p.providerName || p.providerId || 'Experiences';
    const arr = groups.get(key) ?? [];
    arr.push(p);
    groups.set(key, arr);
  }
  return Array.from(groups, ([name, items]) => ({ name, passes: items }));
}

/** The chosen pass as the canonical items[] array (identical across select/init/confirm). */
function buildItems(pass: Pass, quantity: number, addOnIds: string[]): PassItemSelection[] {
  const item: PassItemSelection = { itemId: pass.itemId, quantity };
  if (pass.parentItemId) item.parentItemId = pass.parentItemId;
  if (addOnIds.length) item.addOnIds = addOnIds;
  return [item];
}

function BookPassPage() {
  const navigate = useNavigate();

  const [txnId, setTxnId] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [visitTs, setVisitTs] = useState(''); // RFC3339, sent as visitDate / visitTimestamp
  const [selected, setSelected] = useState<Pass | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addOnIds, setAddOnIds] = useState<string[]>([]);
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState(auth.currentUser?.email ?? '');
  const [formId, setFormId] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [payNotice, setPayNotice] = useState('');

  // Google sign-in supplies no phone; the operator-set booking contact does. See lib/contact.ts.
  const [phone] = useContactPhone();
  const { draft, timedOut, resumePolling } = usePassDraft(txnId);

  const searchMut = useMutation({
    mutationFn: passesApi.search,
    onSuccess: (ack) => setTxnId(ack.transactionId),
  });
  // Each action reuses the txn; the draft poll stops once a step completes, so resume
  // polling on success to pick up the next callback (on_select / on_init / on_confirm).
  const selectMut = useMutation({
    mutationFn: passesApi.select,
    onSuccess: () => void resumePolling(),
  });
  const initMut = useMutation({
    mutationFn: passesApi.init,
    onSuccess: () => void resumePolling(),
  });
  const confirmMut = useMutation({ mutationFn: passesApi.confirm });

  // Navigate to the entry pass once confirm completes and the order id lands.
  useEffect(() => {
    if (draft?.step === 'confirm' && draft.status === 'completed' && draft.orderId) {
      void navigate({ to: '/passes/$id', params: { id: draft.orderId } });
    }
  }, [draft, navigate]);

  function reset() {
    setTxnId(null);
    setSelected(null);
    setQuantity(1);
    setAddOnIds([]);
    setFormId('');
    setSubmissionId('');
    setPayNotice('');
  }

  function handleBack() {
    if (txnId) reset();
    else void navigate({ to: '/' });
  }

  function handleBrowse(params: BrowseParams) {
    reset();
    setCategory(params.category);
    setVisitTs(params.date);
    searchMut.mutate({
      cityCode: DEFAULT_CITY,
      category: params.category,
      gps: params.gps || DEFAULT_GPS,
      visitDate: params.date,
    });
  }

  function pickPass(p: Pass) {
    setSelected(p);
    setQuantity(1);
    setAddOnIds([]);
  }

  function toggleAddOn(id: string) {
    setAddOnIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function handleSelect() {
    if (!txnId || !selected) return;
    selectMut.mutate({
      transactionId: txnId,
      providerId: selected.providerId,
      bppId: selected.bppId,
      bppUri: selected.bppUri,
      fulfillmentId: selected.fulfillmentId,
      items: buildItems(selected, quantity, addOnIds),
      visitTimestamp: visitTs,
      formId: formId || undefined,
      submissionId: submissionId || undefined,
    });
  }

  async function handleFormSubmit(id: string) {
    if (!txnId || !draft?.form) return;
    const fid = draft.form.id;
    await passesApi.submitForm({ transactionId: txnId, formId: fid, submissionId: id });
    setFormId(fid);
    setSubmissionId(id);
    // A form surfaced at on_init (after init already ran) must re-init so the BPP re-quotes
    // with the submission before we charge; a form at on_select flows into the normal init.
    if (draft.step === 'init') runInit({ formId: fid, submissionId: id });
  }

  function runInit(form?: { formId: string; submissionId: string }) {
    if (!txnId || !selected) return;
    initMut.mutate({
      transactionId: txnId,
      providerId: selected.providerId,
      fulfillmentId: selected.fulfillmentId,
      items: buildItems(selected, quantity, addOnIds),
      visitTimestamp: visitTs,
      billingName: billingName.trim() || customerName() || 'Guest',
      billingPhone: phone,
      billingEmail: customerEmail(billingEmail) || undefined,
      formId: form?.formId ?? (formId || undefined),
      submissionId: form?.submissionId ?? (submissionId || undefined),
    });
  }

  async function handlePay() {
    if (!txnId || !selected) return;
    setPayNotice('');
    try {
      const order = await passesApi.paymentOrder(txnId);
      const success = await openRazorpayCheckout(
        order,
        {
          name: billingName.trim() || customerName() || 'Guest',
          contact: phone,
          email: razorpayEmail(billingEmail),
        },
        'Entry pass'
      );
      await confirmMut.mutateAsync({
        transactionId: txnId,
        providerId: selected.providerId,
        fulfillmentId: selected.fulfillmentId,
        items: buildItems(selected, quantity, addOnIds),
        visitTimestamp: visitTs,
        billingName: billingName.trim() || customerName() || 'Guest',
        billingPhone: phone,
        billingEmail: customerEmail(billingEmail) || undefined,
        formId: formId || undefined,
        submissionId: submissionId || undefined,
        razorpayOrderId: success.razorpay_order_id,
        razorpayPaymentId: success.razorpay_payment_id,
        razorpaySignature: success.razorpay_signature,
      });
      void resumePolling(); // resume polling for on_confirm → entry pass
    } catch (err) {
      if (err instanceof PaymentCancelledError) {
        setPayNotice('Payment cancelled — try again.');
      } else {
        setPayNotice(getErrorMessage(err));
      }
    }
  }

  const actionPending =
    searchMut.isPending || selectMut.isPending || initMut.isPending || confirmMut.isPending;

  // Derive the wizard view from the draft.
  const grouped = useMemo(() => groupByProvider(draft?.passes ?? []), [draft?.passes]);
  const showResults = draft?.step === 'search' && draft.status === 'completed';
  // Gate on THIS form's id (not a global "submitted" flag) so a distinct form surfaced
  // later in the flow can't be skipped because an earlier one was already submitted.
  const needsForm = Boolean(draft?.form?.required) && draft?.form?.id !== formId;
  // The BPP may surface the xinput form on on_select OR on_init (the backend reads
  // on_init first, then on_select), so gate the form on either completed step and keep
  // it ahead of both billing and pay — a required-but-unsubmitted form can't be skipped.
  const showForm =
    draft?.status === 'completed' &&
    (draft.step === 'select' || draft.step === 'init') &&
    needsForm;
  const showBilling = draft?.step === 'select' && draft.status === 'completed' && !needsForm;
  const showReview = draft?.step === 'init' && draft.status === 'completed' && !needsForm;
  const polling =
    !!txnId && draft?.status !== 'completed' && draft?.status !== 'error' && !timedOut;
  const draftErrored = draft?.status === 'error';

  const stepIndex = showReview ? 3 : showBilling || showForm ? 2 : showResults ? 1 : 0;
  const screenProps = {
    title: 'Entry passes',
    accent: 'pass' as const,
    onBack: handleBack,
    right: <AccountButton />,
    steps: { total: 4, current: stepIndex },
  };

  // ONDC TRV14 disabled on this backend → every /passes/* call 503s.
  if (isHttpStatus(searchMut.error, 503)) {
    return (
      <Screen {...screenProps}>
        <EmptyState
          icon="sparkle"
          title="Entry passes aren't available right now"
          description="Entry passes are currently offered in Delhi only. Support for more cities is on the way — please check back soon."
        />
      </Screen>
    );
  }

  return (
    <Screen {...screenProps}>
      <div className="wb-col wb-gap-16">
        <div className="wb-col wb-gap-6">
          <span className="wb-eyebrow">
            {category ? `Entry passes · ${categoryLabel(category)}` : 'Entry passes'}
          </span>
          <h1 className="wb-h1">Discover experiences</h1>
        </div>

        {/* Discovery */}
        {!txnId && <CategoryPicker onBrowse={handleBrowse} pending={searchMut.isPending} />}

        {/* Search failure (non-503 — 503 is handled by the disabled-region gate above) */}
        {searchMut.error && !isHttpStatus(searchMut.error, 503) && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{
              color: 'var(--danger)',
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--danger) 30%, transparent)',
            }}
          >
            {getErrorMessage(searchMut.error)}
          </div>
        )}

        {/* Polling / waiting */}
        {polling && <ProgressLabel label={stepLabel(draft?.step)} />}

        {/* Timeout */}
        {timedOut && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ color: 'var(--honey-700)', background: 'var(--honey-wash-2)' }}
          >
            {draft?.step === 'confirm'
              ? 'Payment received — generating your entry pass. This can take a moment.'
              : 'Taking longer than usual.'}{' '}
            <button
              type="button"
              onClick={() => void resumePolling()}
              className="wb-strong"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {draft?.step === 'confirm' ? 'Check now' : 'Retry'}
            </button>
          </div>
        )}

        {/* Per-step error */}
        {draftErrored && draft?.error && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-8 wb-small"
            style={{
              color: 'var(--danger)',
              background: 'color-mix(in oklab, var(--danger) 10%, var(--surface))',
              borderColor: 'color-mix(in oklab, var(--danger) 30%, transparent)',
            }}
          >
            <p>{draft.error.message}</p>
            <div>
              <Button variant="secondary" size="sm" onClick={reset}>
                Start over
              </Button>
            </div>
          </div>
        )}

        {/* Results — passes grouped by venue/provider */}
        {showResults && (
          <div className="wb-col wb-gap-20">
            {grouped.length === 0 ? (
              <EmptyState
                icon="sparkle"
                title="No experiences found"
                description="Try another category or a different location."
                action={{ label: 'Start over', onClick: reset }}
              />
            ) : (
              grouped.map((g) => (
                <div key={g.name} className="wb-col wb-gap-12">
                  <h2 className="wb-h3">{g.name}</h2>
                  {g.passes.map((p) => (
                    <PassOptionCard
                      key={p.itemId}
                      pass={p}
                      selected={selected?.itemId === p.itemId}
                      onSelect={() => pickPass(p)}
                    />
                  ))}
                </div>
              ))
            )}

            {/* Selected-pass panel: qty + add-ons + continue */}
            {selected && (
              <div className="wb-card wb-card--pad wb-col wb-gap-14">
                <AddOnToggles
                  addOns={selected.addOns ?? []}
                  selected={addOnIds}
                  onToggle={toggleAddOn}
                />
                <div className="wb-row wb-between">
                  <QuantityStepper value={quantity} onChange={setQuantity} />
                  <Button variant="primary" onClick={handleSelect} disabled={actionPending}>
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* xinput additional-details form (when the BPP requires it) */}
        {showForm && draft?.form && (
          <XInputFormGate form={draft.form} onSubmit={handleFormSubmit} />
        )}

        {/* Visitor details (after select, no form pending) */}
        {showBilling && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-14">
            <h2 className="wb-h2">Visitor details</h2>
            {draft?.quote && (
              <p className="wb-sub">
                Total:{' '}
                <span className="wb-strong" style={{ color: 'var(--ink)' }}>
                  {formatMoney(draft.quote.total)}
                </span>
              </p>
            )}
            <Field
              label="Name"
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              placeholder="Visitor's full name"
              hint="Use the visitor’s name — some venues print it on the pass or check it at entry."
            />
            <Field
              label="Email (optional — for your payment receipt)"
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <p className="wb-tiny">Passes sent to {phone}</p>
            <Button variant="primary" block onClick={() => runInit()} disabled={actionPending}>
              Continue
            </Button>
          </div>
        )}

        {/* Review + Pay (after init) */}
        {showReview && selected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <h2 className="wb-h2">Review</h2>
            <div>
              <SumRow label="Experience">{selected.name}</SumRow>
              <SumRow label="Quantity">{quantity}</SumRow>
              {addOnIds.length > 0 && <SumRow label="Add-ons">{addOnIds.length}</SumRow>}
              <hr className="wb-divider" style={{ margin: '6px 0' }} />
              <SumRow label="Total" big>
                {formatMoney(draft?.quote?.total ?? selected.price)}
              </SumRow>
            </div>
            {payNotice && (
              <p className="wb-small" style={{ color: 'var(--danger)' }}>
                {payNotice}
              </p>
            )}
            <Button variant="primary" block onClick={handlePay} disabled={confirmMut.isPending}>
              {confirmMut.isPending ? 'Confirming…' : 'Pay'}
            </Button>
          </div>
        )}
      </div>
    </Screen>
  );
}

function stepLabel(step?: string): string {
  return step === 'select'
    ? 'Getting your quote…'
    : step === 'init'
      ? 'Finalizing price…'
      : step === 'confirm'
        ? 'Confirming your pass…'
        : 'Finding experiences…';
}
